import { formatFuer } from '@fh/shared/media'

import {
  entschluesseln, entschluesselnStrom, metaEntschluesseln, metaVerschluesseln,
  mitSchluessel, neuerAnhangSchluessel, teilSchluessel,
} from '../crypto/rahmen.js'
import { t } from '../i18n/t.js'
import { bildAufbereiten, kannAufbereiten } from './bild.js'
import { blobUrl } from './quellen.js'
import { kannVorschau, medienVorschau } from './video.js'
import { hochladen, type UploadFortschritt } from './upload.js'

/**
 * Anhänge im Chat (Zone A).
 *
 * ## Wie die Teile zusammenhängen
 *
 * Der Server hat seinen Teil seit Phase 6 fertig: die Tabelle `attachment`,
 * bis zu zehn Anhänge je Nachricht, `blobId` + `thumbBlobId` + `metaEnv`. Er
 * hat nur nie einen Aufrufer gehabt. Was hier entsteht, ist die andere Hälfte.
 *
 * Für einen Anhang gibt es **einen** Zufallsschlüssel. Aus ihm leiten sich per
 * HKDF drei ab (`crypto/rahmen.ts`):
 *
 * | Zweck | verschlüsselt |
 * |---|---|
 * | `daten` | die Datei selbst, gerahmt |
 * | `vorschau` | das Vorschaubild, gerahmt |
 * | `meta` | Name, Typ, Größe, Maße — am Stück |
 *
 * **Der Schlüssel selbst reist im Nachrichtentext**, also durch MLS. Damit
 * gilt für einen Anhang genau das, was für die Nachricht gilt: Wer den Raum
 * nicht hat, hat auch die Datei nicht. Der Server sieht drei Pakete Zufall und
 * die Tatsache, dass etwas angehängt wurde.
 *
 * ## Warum der Schlüssel nicht in `metaEnv` steckt
 *
 * `metaEnv` liegt beim Server, MLS-Text nicht. Läge der Dateischlüssel dort,
 * wäre die Verschlüsselung ein Ring, der sich selbst öffnet.
 *
 * ## Warum der Name verschlüsselt wird
 *
 * Ein Dateiname ist selbst eine Auskunft. „Befund Onkologie.pdf" verrät die
 * Diagnose, ohne dass jemand die Datei öffnet.
 */

/** Was im Nachrichtentext steht — und damit durch MLS geht. */
export interface AnhangReferenz {
  blobId: string
  thumbBlobId?: string
  /** base64 des Anhangsschlüssels. Ohne ihn ist der Blob Zufall. */
  k: string
}

/** Was der Server bekommt. */
export interface AnhangEintrag {
  blobId: string
  thumbBlobId?: string
  metaEnv: string
}

/** Was in `metaEnv` steht — verschlüsselt. */
export interface AnhangInfo {
  name: string
  mime: string
  groesse: number
  breite?: number
  hoehe?: number
  /** Sekunden, nur bei Video und Ton. */
  dauer?: number
}

interface Stuecke {
  daten: Blob
  vorschau: Blob | null
  breite: number
  hoehe: number
  dauer: number
}

/**
 * Ein Bild: neu kodiert, damit die Ortsangabe wegfällt.
 *
 * Kein stiller Rückfall auf die Originaldatei. Zuerst stand hier genau das —
 * kann der Browser das Bild nicht aufbereiten, wird es eben unverändert
 * hochgeladen. Das wäre ein Foto **mit** den GPS-Koordinaten darin,
 * verschlüsselt gegenüber dem Server und offen gegenüber allen im Raum. Wer
 * ein Bild vom eigenen Balkon schickt, teilte damit seine Wohnadresse, ohne es
 * zu erfahren. Lieber eine Absage, die man liest, als eine Zusage, die nicht
 * gilt.
 */
async function bildStuecke(datei: File, ortBehalten: boolean): Promise<Stuecke> {
  if (!kannAufbereiten()) {
    throw new AnhangAbgelehnt(
      t('Dieses Gerät kann Fotos nicht für den Versand vorbereiten. Ohne das Entfernen der Ortsangabe wird kein Bild verschickt.'))
  }
  try {
    const a = await bildAufbereiten(datei, { ortBehalten })
    return { daten: a.daten, vorschau: a.vorschau, breite: a.breite, hoehe: a.hoehe, dauer: 0 }
  } catch {
    throw new AnhangAbgelehnt(t('Dieses Bild konnte nicht gelesen werden.'))
  }
}

/**
 * Video und Ton: unverändert, aber mit Vorschaubild und Dauer.
 *
 * Die Datei geht durch, wie sie ist — umkodieren hieße, einen Videoumwandler
 * mitzuliefern. Deshalb steht davor die Nachfrage in `anhangVorbereiten`.
 *
 * Ein fehlgeschlagenes Vorschaubild ist kein Grund, den Versand abzubrechen:
 * Anders als bei Bildern hängt daran keine Zusage, nur die Bequemlichkeit,
 * das Video in der Liste zu erkennen.
 */
async function medienStuecke(datei: File): Promise<Stuecke> {
  if (!kannVorschau()) return { daten: datei, vorschau: null, breite: 0, hoehe: 0, dauer: 0 }
  const v = await medienVorschau(datei)
  return { daten: datei, vorschau: v.vorschau, breite: v.breite, hoehe: v.hoehe, dauer: v.dauer }
}

export const BILD_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/gif']
export const VIDEO_MIMES = ['video/mp4', 'video/quicktime', 'video/webm']
export const TON_MIMES = ['audio/mpeg', 'audio/ogg']

/** Was im Chat mitgeschickt werden darf. Der Server kennt dieselben Formate. */
export const ERLAUBTE_MIMES = [...BILD_MIMES, ...VIDEO_MIMES, ...TON_MIMES]

export function istErlaubt(mime: string): boolean {
  return ERLAUBTE_MIMES.includes(mime)
}

export function istBild(mime: string): boolean { return BILD_MIMES.includes(mime) }
export function istVideo(mime: string): boolean { return VIDEO_MIMES.includes(mime) }
export function istTon(mime: string): boolean { return TON_MIMES.includes(mime) }

/**
 * Obergrenze im Chat — enger als das, was der Server nimmt (500 MB).
 *
 * Ein Anhang wird vor dem Abspielen **vollständig** geladen und entschlüsselt;
 * wahlfreier Zugriff je Rahmen ist in ADR-011 als offen vermerkt und ist es
 * geblieben. 100 MB sind auf einer Mobilverbindung schon mehrere Minuten
 * Warten — darüber wäre der Anhang eine Zumutung, keine Nachricht. Wer ein
 * ganzes Urlaubsvideo teilen will, nimmt den Dateibereich.
 */
export const CHAT_MAX_BYTES = 100 * 1024 * 1024

/**
 * Formate, deren Ortsangaben wir NICHT entfernen können.
 *
 * Bei Fotos wird das Bild neu kodiert, dabei fällt alles Beiwerk weg. Ein
 * Video neu zu kodieren hieße, einen Videoumwandler in die App zu holen —
 * mehrere Megabyte Abhängigkeit im Pfad jedes Anhangs, für ein Telefon, das
 * ohnehin am Anschlag ist. Also bleibt der Ort drin, und dann ist das
 * Mindeste, es zu sagen und zu fragen (`ortBekannt`).
 *
 * `webm`, `mp3` und `ogg` stehen nicht auf dieser Liste: Sie führen laut
 * `FORMATE` keine solchen Angaben.
 */
export function traegtOrt(mime: string): boolean {
  return formatFuer(mime)?.exif === true && !istBild(mime)
}

export class AnhangAbgelehnt extends Error {
  constructor(nachricht: string) {
    super(nachricht)
    this.name = 'AnhangAbgelehnt'
  }
}

function alsB64(roh: Uint8Array): string {
  let s = ''
  for (const b of roh) s += String.fromCharCode(b)
  return btoa(s)
}

function ausB64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
}

/**
 * Bereitet eine Datei vor: aufbereiten, verschlüsseln, hochladen.
 *
 * Reihenfolge mit Absicht — **EXIF fällt vor der Verschlüsselung weg**. Ein
 * verschlüsseltes Foto mit GPS-Koordinaten darin ist gegenüber dem Server
 * dicht und gegenüber der Familie offen; die Wohnadresse steht dann in einer
 * Datei, die alle im Raum öffnen können. Der Schalter dafür heißt
 * `ortBehalten` und ist aus.
 */
export async function anhangVorbereiten(
  datei: File,
  opts: {
    ortBehalten?: boolean
    /**
     * Für Video und Ton: Der Mensch weiß, dass der Ort drinbleiben kann, und
     * will trotzdem senden. Ohne das wird abgelehnt — siehe `traegtOrt`.
     */
    ortBekannt?: boolean
    onFortschritt?: (f: UploadFortschritt) => void
  } = {},
): Promise<{ eintrag: AnhangEintrag; referenz: AnhangReferenz; info: AnhangInfo }> {
  const format = formatFuer(datei.type)
  if (!format || !istErlaubt(format.mime)) {
    throw new AnhangAbgelehnt(
      t('Das kann im Chat nicht mitgeschickt werden. Bilder, Videos und Tonaufnahmen gehen.'))
  }
  if (datei.size > Math.min(format.maxBytes, CHAT_MAX_BYTES)) {
    throw new AnhangAbgelehnt(
      t('Diese Datei ist für den Chat zu groß. Über den Dateibereich geht sie.'))
  }
  if (traegtOrt(format.mime) && !opts.ortBekannt) {
    // Kein stiller Versand. Der Aufrufer muss gefragt haben.
    throw new AnhangAbgelehnt(
      t('Videos können Aufnahmeort und -zeit enthalten. Die lassen sich hier nicht entfernen — bitte bestätige, dass du es trotzdem senden willst.'))
  }

  const aufbereitet = istBild(format.mime)
    ? await bildStuecke(datei, opts.ortBehalten ?? false)
    : await medienStuecke(datei)

  const anhang = neuerAnhangSchluessel()

  const paket = await mitSchluessel(aufbereitet.daten, await teilSchluessel(anhang, 'daten'))
  const blob = await hochladen(paket, { zone: 'A', ...(opts.onFortschritt ? { onFortschritt: opts.onFortschritt } : {}) })

  let thumbBlobId: string | undefined
  if (aufbereitet.vorschau) {
    const vp = await mitSchluessel(aufbereitet.vorschau, await teilSchluessel(anhang, 'vorschau'))
    thumbBlobId = (await hochladen(vp, { zone: 'A', thumbnail: true })).id
  }

  const info: AnhangInfo = {
    name: datei.name,
    mime: format.mime,
    groesse: aufbereitet.daten.size,
    ...(aufbereitet.breite ? { breite: aufbereitet.breite, hoehe: aufbereitet.hoehe } : {}),
    ...(aufbereitet.dauer ? { dauer: aufbereitet.dauer } : {}),
  }

  return {
    eintrag: {
      blobId: blob.id,
      ...(thumbBlobId ? { thumbBlobId } : {}),
      metaEnv: await metaVerschluesseln(info, await teilSchluessel(anhang, 'meta')),
    },
    referenz: { blobId: blob.id, ...(thumbBlobId ? { thumbBlobId } : {}), k: alsB64(anhang) },
    info,
  }
}

/*
 * --- Der Nachrichtentext, wenn etwas dranhängt ------------------------------
 *
 * Die Schlüssel müssen durch MLS. Es gibt genau einen Kanal, der das tut: den
 * Nachrichtentext. Also trägt er beides — Text und Referenzen — sobald ein
 * Anhang dabei ist.
 *
 * Die Marke ist ein Steuerzeichen. Sie lässt sich nicht tippen, nicht
 * einfügen und nicht versehentlich schreiben; eine Nachricht, die zufällig wie
 * JSON aussieht („{"v":1}"), bleibt deshalb ein ganz normaler Text. Alte
 * Nachrichten ohne Marke ebenso — sie sind der Normalfall und dürfen nichts
 * kosten.
 */

/**
 * Exportiert, damit Tests sie nicht abschreiben müssen.
 *
 * Sie beginnt mit U+0001 — abgeschrieben sah sie im Test aus wie
 * `fh-anhang1:`, und weil das eben NICHT die Marke ist, prüfte der Test
 * denselben Zweig wie eine ganz normale Nachricht und war aus dem falschen
 * Grund grün. Eine Konstante kann man nicht falsch abschreiben.
 */
export const MARKE = 'fh-anhang1:'

/**
 * Die Übersetzung einer Nachricht, wie sie mitreist.
 *
 * `sprache` ist die Sprache des ORIGINALS, nicht die der Übersetzung. So kann
 * jeder Leser entscheiden, welche der beiden Zeilen für ihn die große ist —
 * ohne selbst erkennen zu müssen, was da geschrieben steht.
 */
export interface Uebersetzung {
  sprache: 'de' | 'en'
  text: string
}

export interface Inhalt {
  text: string
  anhaenge: AnhangReferenz[]
  uebersetzung?: Uebersetzung
  /**
   * Kein Text, sondern die Auskunft, warum keiner da ist.
   *
   * Steht hier, damit die Blase es anders zeigen kann als eine Nachricht. Ohne
   * diese Unterscheidung sah eine unlesbare Nachricht aus wie eine, die jemand
   * mit dem Wortlaut „(Konnte nicht entschlüsselt werden)" geschickt hat — und
   * Dan sah nach dem Behobenen weiter lauter Fehlermeldungen im Chat stehen.
   */
  unlesbar?: boolean
}

export function inhaltPacken(
  text: string,
  anhaenge: AnhangReferenz[],
  uebersetzung?: Uebersetzung | null,
): string {
  if (anhaenge.length === 0 && !uebersetzung) return text
  return MARKE + JSON.stringify({
    text, anhaenge, ...(uebersetzung ? { uebersetzung } : {}),
  })
}

export function inhaltLesen(roh: string): Inhalt {
  if (!roh.startsWith(MARKE)) return { text: roh, anhaenge: [] }
  try {
    const gelesen = JSON.parse(roh.slice(MARKE.length)) as {
      text?: unknown; anhaenge?: unknown; uebersetzung?: unknown
    }
    const anhaenge = Array.isArray(gelesen.anhaenge)
      ? (gelesen.anhaenge as AnhangReferenz[]).filter(
        (a) => typeof a?.blobId === 'string' && typeof a?.k === 'string')
      : []

    const u = gelesen.uebersetzung as Uebersetzung | undefined
    const uebersetzung = u && typeof u.text === 'string' && (u.sprache === 'de' || u.sprache === 'en')
      ? { sprache: u.sprache, text: u.text }
      : undefined

    return {
      text: typeof gelesen.text === 'string' ? gelesen.text : '',
      anhaenge,
      ...(uebersetzung ? { uebersetzung } : {}),
    }
  } catch {
    // Kaputt oder aus einer neueren Fassung: lieber der rohe Text als eine
    // leere Blase. Der Nutzer sieht dann etwas Merkwürdiges statt nichts.
    return { text: roh, anhaenge: [] }
  }
}

/** Die Angaben zu einem Anhang — ohne die Datei selbst zu holen. */
export async function anhangInfo(metaEnv: string, k: string): Promise<AnhangInfo> {
  return metaEntschluesseln<AnhangInfo>(metaEnv, await teilSchluessel(ausB64(k), 'meta'))
}

/**
 * Holt einen Anhang und gibt eine anzeigbare URL zurück.
 *
 * `welche: 'vorschau'` lädt nur das Vorschaubild — ein paar Kilobyte statt ein
 * paar Megabyte. Auf einer Mobilverbindung ist das der Unterschied zwischen
 * einem Chat, der sich öffnen lässt, und einem, der lädt.
 *
 * Der Aufrufer muss die URL mit `URL.revokeObjectURL` wieder freigeben; sonst
 * behält der Browser jedes je angezeigte Bild im Speicher.
 */
export async function anhangHolen(
  ref: AnhangReferenz,
  welche: 'daten' | 'vorschau',
): Promise<string> {
  const blobId = welche === 'vorschau' ? ref.thumbBlobId : ref.blobId
  if (!blobId) throw new AnhangAbgelehnt(t('Für diese Datei gibt es kein Vorschaubild.'))

  const url = await blobUrl(blobId)
  if (!url) throw new AnhangAbgelehnt(t('Die Datei ist nicht mehr verfügbar.'))

  const res = await fetch(url)
  if (!res.ok) throw new AnhangAbgelehnt(t('Die Datei konnte nicht geladen werden.'))

  const key = await teilSchluessel(ausB64(ref.k), welche)
  /*
   * Über den Strom, wenn der Browser einen liefert.
   *
   * `res.arrayBuffer()` legt die ganze Datei im JavaScript-Speicher ab, und
   * die entschlüsselte Kopie kommt daneben — bei einem Video von 80 MB sind
   * das 160 MB auf einem Telefon, das vielleicht 2 GB hat. Im Strom wandert
   * immer nur ein Rahmen durch, und der `Blob` am Ende gehört dem Browser,
   * der ihn notfalls auf die Platte auslagert.
   *
   * Der Rückfall ist kein Schmuck: happy-dom und ältere Browser liefern
   * `res.body` nicht.
   */
  const klar = res.body
    ? await entschluesselnStrom(res.body, key)
    : await entschluesseln(await res.arrayBuffer(), key)
  return URL.createObjectURL(klar)
}
